import { useState, useEffect } from 'react';
import { Users, MessageSquare, Phone, Mail, Video, MapPin, StickyNote } from 'lucide-react';
import { api } from '../../services/api';

// ZYN-311: abas do ContaFormModal extraídas — cada uma é auto-contida
// (busca seus próprios dados via contaId, sem estado compartilhado).

export function FiliaisTab({ contaId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.getContaFiliais(contaId).then(setRows).finally(() => setLoading(false));
  }, [contaId]);
  if (loading) return <div className="text-sm text-gray-400">Carregando...</div>;
  if (rows.length === 0) {
    return <div className="text-sm text-gray-500 py-6 text-center">
      Nenhuma filial vinculada. Para ligar uma filial, edite a conta da filial e selecione esta como "Conta matriz".
    </div>;
  }
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">CNPJ</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cidade/UF</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">ESN</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map(r => (
            <tr key={r.id}>
              <td className="px-3 py-2 font-medium">{r.nome}</td>
              <td className="px-3 py-2 font-mono text-xs text-gray-500">{r.cnpj}</td>
              <td className="px-3 py-2 text-gray-600">{[r.cidade, r.estado].filter(Boolean).join('/') || '—'}</td>
              <td className="px-3 py-2 text-gray-600">{r.esn_nome || '—'}</td>
              <td className="px-3 py-2 text-gray-600">{r.status_cliente || 'ativo'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function OportunidadesTab({ contaId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.getContaOportunidades(contaId).then(setRows).finally(() => setLoading(false));
  }, [contaId]);

  if (loading) return <div className="text-sm text-gray-400">Carregando...</div>;
  if (rows.length === 0) return <div className="text-sm text-gray-500 py-6 text-center">Nenhuma oportunidade vinculada.</div>;

  const fmt = v => (v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Funil</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Estágio</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Produto</th>
            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">SaaS</th>
            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Adesão</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Previsão</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">ESN</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map(r => (
            <tr key={r.id} className={r.stage === 'Fechado/Ganho' ? 'bg-emerald-50/40' : r.stage === 'Perdido' ? 'bg-red-50/40 text-gray-400' : ''}>
              <td className="px-3 py-2 text-xs uppercase tracking-wide">{r.funnel_type === 'new_names' ? 'Novos' : 'Base'}</td>
              <td className="px-3 py-2">{r.stage}</td>
              <td className="px-3 py-2 text-gray-600">{r.produto_nome || '—'}</td>
              <td className="px-3 py-2 text-right">{fmt(r.valor_saas)}</td>
              <td className="px-3 py-2 text-right">{fmt(r.valor_adesao)}</td>
              <td className="px-3 py-2 text-gray-600 text-xs">{r.previsao_fechamento || '—'}</td>
              <td className="px-3 py-2 text-gray-600 text-xs">{r.esn_nome || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function InteracoesTab({ contaId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.getContaInteractions(contaId).then(setRows).finally(() => setLoading(false));
  }, [contaId]);

  const CANAL_ICON = {
    'Telefone': Phone, 'WhatsApp': MessageSquare, 'E-mail': Mail,
    'Videochamada': Video, 'Reunião Presencial': Users, 'Visita Técnica': MapPin,
    'Nota Interna': StickyNote,
  };

  if (loading) return <div className="text-sm text-gray-400">Carregando...</div>;
  if (rows.length === 0) return <div className="text-sm text-gray-500 py-6 text-center">Nenhuma interação registrada em oportunidades desta conta.</div>;

  return (
    <div className="space-y-2">
      {rows.map(r => {
        const Icon = CANAL_ICON[r.canal] || MessageSquare;
        return (
          <div key={r.id} className="border border-gray-200 rounded-lg px-3 py-2.5 hover:bg-gray-50">
            <div className="flex items-start gap-2">
              <Icon size={14} className="text-[#00DBFF] mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-semibold text-[#002233]">{r.canal}</span>
                  <span className="text-xs text-gray-400">•</span>
                  <span className="text-xs text-gray-500">{r.data_interacao ? new Date(r.data_interacao).toLocaleString('pt-BR') : ''}</span>
                  {r.lead_titulo && (
                    <>
                      <span className="text-xs text-gray-400">•</span>
                      <span className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                        Opp #{r.lead_id}: {r.lead_titulo}
                      </span>
                    </>
                  )}
                </div>
                <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{r.descricao}</p>
                {r.proxima_acao_descricao && (
                  <p className="text-xs text-amber-700 mt-1">
                    ⏭ Próxima: {r.proxima_acao_descricao}
                    {r.proxima_acao_data && ` (${r.proxima_acao_data})`}
                  </p>
                )}
                <div className="text-xs text-gray-400 mt-1">{r.esn_nome || '—'}</div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function TimelineTab({ contaId }) {
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);

  async function load(c) {
    setLoading(true);
    try {
      const res = await api.getContaTimeline(contaId, c);
      setItems(prev => c ? [...prev, ...res.items] : res.items);
      setCursor(res.next_cursor);
      setHasMore(!!res.next_cursor);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(null);   }, [contaId]);

  const TYPE_META = {
    'stage_change':  { label: 'Mudança de estágio', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
    'update':        { label: 'Edição',             cls: 'bg-gray-50 text-gray-700 border-gray-200' },
    'create':        { label: 'Criação',            cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    'delete':        { label: 'Exclusão',           cls: 'bg-red-50 text-red-700 border-red-200' },
    'interaction':   { label: 'Interação',          cls: 'bg-[#00DBFF]/10 text-[#002233] border-[#00DBFF]/30' },
    'whatsapp':      { label: 'WhatsApp',           cls: 'bg-green-50 text-green-700 border-green-200' },
    'proposal':      { label: 'Proposta',           cls: 'bg-purple-50 text-purple-700 border-purple-200' },
    'comment':       { label: 'Comentário',         cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  };

  function renderPayload(it) {
    let p = {};
    try { p = typeof it.payload === 'string' ? JSON.parse(it.payload) : (it.payload || {}); } catch { /* payload não é JSON válido — ignora */ }
    switch (it.type) {
      case 'stage_change': return `${p.old_value || '—'} → ${p.new_value || '—'}`;
      case 'interaction':  return `${p.canal || ''}: ${p.descricao || ''}`;
      case 'whatsapp':     return `${p.phone || ''}: ${p.message || ''}`;
      case 'proposal':     return `${p.title || ''} (${p.status || ''})`;
      case 'comment':      return p.content || '';
      case 'update':       return `${p.field || ''}: ${p.old_value || '—'} → ${p.new_value || '—'}`;
      default:             return '';
    }
  }

  if (loading && items.length === 0) return <div className="text-sm text-gray-400">Carregando...</div>;
  if (items.length === 0) return <div className="text-sm text-gray-500 py-6 text-center">Nenhum evento registrado nas oportunidades desta conta.</div>;

  return (
    <div className="space-y-2">
      {items.map(it => {
        const meta = TYPE_META[it.type] || { label: it.type, cls: 'bg-gray-50 text-gray-700 border-gray-200' };
        return (
          <div key={it.id} className="border border-gray-200 rounded-lg px-3 py-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full border ${meta.cls}`}>
                {meta.label}
              </span>
              <span className="text-xs text-gray-500">{it.at ? new Date(it.at).toLocaleString('pt-BR') : ''}</span>
              {it.actor && <span className="text-xs text-gray-500">• {it.actor}</span>}
              {it.lead_id && (
                <span className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">Opp #{it.lead_id}</span>
              )}
            </div>
            <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{renderPayload(it)}</p>
          </div>
        );
      })}
      {hasMore && (
        <button type="button" onClick={() => load(cursor)} disabled={loading}
          className="w-full text-xs text-[#00DBFF] hover:underline py-2 disabled:opacity-50">
          {loading ? 'Carregando...' : 'Carregar mais'}
        </button>
      )}
    </div>
  );
}
